import styled from 'styled-components';
import { CSSVars } from '../../styles/styles.config';

// Main page wrapper
export const StyledExternalPageWrapper = styled.div`
	background: ${CSSVars.white};
	min-height: 100vh;
`;

// Container wrapper for max-width constraint
export const StyledSectionWrapper = styled.div`
	max-width: 1020px;
	margin: 0 auto;
	padding: 0 20px;
	margin-bottom: 60px;

	&.full-width {
		max-width: 100%;
		padding: 0;
	}
`;

// Shared markdown styles
export const MarkdownStyles = styled.div`
	line-height: 1.7;
	color: inherit;

	p {
		margin-bottom: 2rem;
		&:last-child {
			margin-bottom: 0;
		}
	}

	a {
		color: ${CSSVars.primary500};
		text-decoration: underline;
		&:hover {
			color: ${CSSVars.primary700};
		}
	}

	strong {
		font-weight: 600;
	}

	em {
		font-style: italic;
	}

	ul,
	ol {
		margin: 1rem 0;
		padding-left: 2rem;
		list-style: disc;
	}

	ol {
		list-style: decimal;
	}

	li {
		margin-bottom: 0.5rem;
	}

	h1,
	h2,
	h3,
	h4,
	h5,
	h6 {
		margin: 6rem 0 2rem;
		font-weight: 600;
		line-height: 1.3;

		&:first-child {
			margin-top: 0;
		}
	}

	blockquote {
		border-left: 4px solid ${CSSVars.primary500};
		padding-left: 1rem;
		margin: 1rem 0;
		font-style: italic;
		color: ${CSSVars.gray500};
	}

	code {
		background: ${CSSVars.gray200};
		padding: 0.2rem 0.4rem;
		border-radius: 4px;
		font-family: monospace;
		font-size: 0.9em;
	}

	pre {
		background: ${CSSVars.gray700};
		color: ${CSSVars.white};
		padding: 1rem;
		border-radius: 8px;
		overflow-x: auto;
		margin: 1rem 0;

		code {
			background: transparent;
			padding: 0;
			color: inherit;
		}
	}
`;

// Hero Section Styles
export const StyledHeroSection = styled.section<{ $hasBackground?: boolean }>`
	position: relative;
	padding: 80px 0;
	min-height: 500px;
	background-image: url('/assets/hero-bg.png');
	background-size: contain;
	background-position: center;
	background-repeat: no-repeat;

	/* ${({ $hasBackground }) =>
		$hasBackground &&
		`
		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			right: 0;
			bottom: 0;
			background: rgba(0, 0, 0, 0.5);
			z-index: 0;
		}
	`} */

	.hero-content {
		position: relative;
		z-index: 1;
	}

	h1 {
		font-size: ${CSSVars.fontSize4XL};
		font-weight: 700;
		margin-bottom: 2rem;
		color: ${CSSVars.gray900};
	}

	h2 {
		font-size: ${CSSVars.fontSize2XL};
		font-weight: 400;
		margin-bottom: 2rem;
		color: ${CSSVars.gray600};
	}

	.description {
		font-size: ${CSSVars.fontSizeLG};
		color: ${CSSVars.gray500};
		margin-bottom: 2rem;
	}

	.buttons-wrapper {
		display: flex;
		gap: 1rem;
		flex-wrap: wrap;

		a {
			padding: 16px 32px;
			font-size: ${CSSVars.fontSizeMD};
			font-weight: 600;
		}
	}

	/* hero_1: Simple centered layout, no image */
	&.hero_1 {
		text-align: center;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		min-height: 300px;

		.hero-content {
			max-width: 700px;
			text-align: center;
		}

		.buttons-wrapper {
			justify-content: center;
		}
	}

	/* hero_1_image: Centered with full-width background image */
	&.hero_1_image {
		text-align: center;
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		min-height: 450px;
		margin-left: -20px;
		margin-right: -20px;
		padding-left: 20px;
		padding-right: 20px;
    background-size: cover;

		.hero-content {
			max-width: 700px;
			text-align: center;
		}

		.buttons-wrapper {
			justify-content: center;
		}
	}

	/* hero_2: Centered text with template preview card below */
	&.hero_2 {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 60px;

		.hero-content {
			flex: 1;
			text-align: left;
		}

		.buttons-wrapper {
			justify-content: flex-start;
		}

		.template-preview {
			flex-shrink: 0;
			flex: 1;
			background: ${CSSVars.gray200};
			border-radius: 12px;
			overflow: hidden;
			box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);

			.preview-image {
				width: 100%;
				height: 300px;
				position: relative;
				background: ${CSSVars.gray300};

				img {
					width: 100%;
					height: 100%;
					object-fit: contain;
				}
			}

			.preview-button {
				padding: 12px 20px;
				background: ${CSSVars.primary500};
				color: ${CSSVars.white};
				text-align: center;
				font-weight: 500;
				font-size: ${CSSVars.fontSizeSM};
				display: block;

				&:hover {
					background: ${CSSVars.primary700};
				}
			}
		}
	}

	/* hero_3: Side by side - text left, image right */
	&.hero_3 {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 60px;

		.hero-content {
			flex: 1;
			text-align: left;
		}

		.buttons-wrapper {
			justify-content: flex-start;
		}

		.media-wrapper {
			flex: 1;
			max-width: 450px;
			background: ${CSSVars.gray200};
			border-radius: 12px;
			overflow: hidden;
			box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);

			.media-image {
				width: 100%;
				height: 280px;
				position: relative;
				background: ${CSSVars.gray300};

				img {
					width: 100%;
					height: 100%;
					object-fit: cover;
				}
			}

			.media-button {
				padding: 12px 20px;
				background: ${CSSVars.primary500};
				color: ${CSSVars.white};
				text-align: center;
				font-weight: 500;
				font-size: ${CSSVars.fontSizeSM};
				display: block;

				&:hover {
					background: ${CSSVars.primary700};
				}
			}
		}
	}

	/* hero_4: Side by side - text left, video right */
	&.hero_4 {
		display: flex;
		flex-direction: row;
		align-items: center;
		gap: 60px;

		.hero-content {
			flex: 1;
			text-align: left;
		}

		.buttons-wrapper {
			justify-content: flex-start;
		}

		.media-wrapper {
			flex: 1;
			max-width: 500px;
			border-radius: 12px;
			overflow: hidden;
			box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);

			> div {
				aspect-ratio: 16 / 9;
			}
		}
	}

	@media screen and (max-width: ${CSSVars.breakpointMD}) {

		h1 {
			font-size: ${CSSVars.fontSize3XL};
		}

		h2 {
			font-size: ${CSSVars.fontSizeXL};
		}

		&.hero_2 {
			flex-direction: column;

			.hero-content {
				text-align: left;
			}

			.template-preview {
				width: 100%;

				.preview-image {
					width: 100%;
					height: 220px;
				}
			}
		}

		&.hero_3,
		&.hero_4 {
			flex-direction: column;

			.hero-content {
				text-align: left;
			}

			.media-wrapper {
				max-width: 100%;
				width: 100%;
			}
		}
	}
`;

// Comparison Table Styles
export const StyledComparisonTable = styled.section`

	h2 {
		text-align: left;
		font-size: ${CSSVars.fontSize3XL};
		font-weight: 700;
		margin-bottom: 2rem;
		color: ${CSSVars.gray900};
	}

	.subtitle {
		text-align: left;
		font-size: ${CSSVars.fontSizeLG};
		color: ${CSSVars.gray500};
		margin-bottom: 3rem;
	}

	.table-wrapper {
		background: ${CSSVars.white};
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
		border: 1px solid ${CSSVars.gray200};
	}

	.table-header {
		display: grid;
		grid-template-columns: 2fr 1fr 1fr;
		background: ${CSSVars.gray100};
		border-bottom: 1px solid ${CSSVars.gray200};

		.header-cell {
			padding: 1.5rem;
			font-weight: 600;
			font-size: ${CSSVars.fontSizeMD};
			color: ${CSSVars.gray700};
			text-align: center;

			&:first-child {
				text-align: left;
			}
		}
	}

	.table-row {
		display: grid;
		grid-template-columns: 2fr 1fr 1fr;
		border-bottom: 1px solid ${CSSVars.gray200};

		&:last-child {
			border-bottom: none;
		}

		.row-cell {
			padding: 1.25rem 1.5rem;
			display: flex;
			align-items: center;
			justify-content: center;
			font-size: ${CSSVars.fontSizeSM};
			color: ${CSSVars.gray600};

			&:first-child {
				justify-content: flex-start;
				font-weight: 500;
				color: ${CSSVars.gray700};
			}
		}
	}

	.symbol-yes {
		color: ${CSSVars.success};
		font-size: 1.5rem;
	}

	.symbol-no {
		color: ${CSSVars.error};
		font-size: 1.5rem;
	}

	.buttons-wrapper {
		display: flex;
		gap: 1rem;
		justify-content: center;
		margin-top: 2rem;
	}

	@media screen and (max-width: ${CSSVars.breakpointMD}) {
		.table-header,
		.table-row {
			grid-template-columns: 1.5fr 1fr 1fr;
		}

		.table-header .header-cell,
		.table-row .row-cell {
			padding: 1rem;
			font-size: ${CSSVars.fontSizeXS};
		}
	}
`;

// Sticky Button Styles
export const StyledStickyButton = styled.div`
	position: fixed;
	bottom: 20px;
	right: 20px;
	z-index: 1000;
	animation: slideIn 0.3s ease-out;

	@keyframes slideIn {
		from {
			transform: translateY(100px);
			opacity: 0;
		}
		to {
			transform: translateY(0);
			opacity: 1;
		}
	}

	a {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		padding: 1rem 2rem;
		background: ${CSSVars.primary500};
		color: ${CSSVars.white};
		border-radius: 50px;
		font-weight: 600;
		font-size: ${CSSVars.fontSizeMD};
		box-shadow: 0 4px 20px rgba(45, 160, 237, 0.4);
		transition: all 0.2s ease;

		&:hover {
			background: ${CSSVars.primary700};
			transform: translateY(-2px);
			box-shadow: 0 6px 25px rgba(45, 160, 237, 0.5);
		}
	}
`;

// Logo Slider Styles
export const StyledLogoSlider = styled.section`
	padding: 40px 0;
	overflow: hidden;
	background: ${CSSVars.gray100};

	h2 {
		text-align: left;
		font-size: ${CSSVars.fontSize2XL};
		font-weight: 600;
		margin-bottom: 2rem;
		color: ${CSSVars.gray700};
		padding: 0 20px;
	}

	.slider-track {
		display: flex;
		animation: scroll 30s linear infinite;
		width: fit-content;
	}

	.logo-item {
		flex-shrink: 0;
		padding: 0 40px;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			max-height: 50px;
			width: auto;
			filter: grayscale(100%);
			opacity: 0.7;
			transition: all 0.3s ease;

			&:hover {
				filter: grayscale(0%);
				opacity: 1;
			}
		}

		a {
			display: flex;
			align-items: center;
		}
	}

	@keyframes scroll {
		0% {
			transform: translateX(0);
		}
		100% {
			transform: translateX(-50%);
		}
	}
`;

// Banner Styles
export const StyledBannerSection = styled.section`
	text-align: center;

	.banner-inner {
		padding: 50px;
		background: ${CSSVars.gray100};
		border: 1px solid ${CSSVars.gray200};
		border-radius: 16px;
		text-align: inherit;
	}

	h2 {
		font-size: ${CSSVars.fontSize3XL};
		font-weight: 700;
		margin-bottom: 2rem;
		color: ${CSSVars.gray900};
	}

	.description {
		font-size: ${CSSVars.fontSizeLG};
		color: ${CSSVars.gray600};
		margin-bottom: 2rem;
	}

	.buttons-wrapper {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;

		a {
			padding: 16px 32px;
			font-size: ${CSSVars.fontSizeMD};
			font-weight: 600;
		}
	}

	@media screen and (max-width: ${CSSVars.breakpointMD}) {
		.banner-inner {
			padding: 30px 20px;
		}

		h2 {
			font-size: ${CSSVars.fontSize2XL};
		}
	}
`;

// Benefits Styles
export const StyledBenefits = styled.section`

	h2 {
		text-align: left;
		font-size: ${CSSVars.fontSize3XL};
		font-weight: 700;
		margin-bottom: 2rem;
		color: ${CSSVars.gray900};
	}

	.subtitle {
		text-align: left;
		font-size: ${CSSVars.fontSizeLG};
		color: ${CSSVars.gray500};
		margin-bottom: 3rem;
	}

	.benefits-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 30px;
	}

	.benefit-card {
		text-align: left;
		padding: 30px;
		background: ${CSSVars.white};
		border: 1px solid ${CSSVars.gray200};
		border-radius: 12px;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);

		.icon-wrapper {
			width: 50px;
			height: 50px;
			margin-bottom: 1.5rem;
			display: flex;
			align-items: center;
			justify-content: center;
			background: ${CSSVars.primary100};
			border-radius: 12px;

			img {
				width: 28px;
				height: 28px;
			}
		}

		h3 {
			font-size: ${CSSVars.fontSizeLG};
			font-weight: 600;
			margin-bottom: 0.75rem;
			color: ${CSSVars.gray900};
		}

		.benefit-text {
			font-size: ${CSSVars.fontSizeSM};
			color: ${CSSVars.gray500};
			line-height: 1.6;
		}
	}

	@media screen and (max-width: ${CSSVars.breakpointLG}) {
		.benefits-grid {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	@media screen and (max-width: ${CSSVars.breakpointMD}) {
		.benefits-grid {
			grid-template-columns: 1fr;
		}
	}
`;

// Video Styles
export const StyledVideoSection = styled.section<{ $size: 'big' | 'small' }>`
	text-align: left;

	h2 {
		font-size: ${CSSVars.fontSize3XL};
		font-weight: 700;
		margin-bottom: 2rem;
		color: ${CSSVars.gray900};
	}

	.subtitle {
		font-size: ${CSSVars.fontSizeLG};
		color: ${CSSVars.gray500};
		margin-bottom: 3rem;
	}

	.video-wrapper {
		max-width: ${({ $size }) => ($size === 'big' ? '100%' : '700px')};
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);

		> div {
			aspect-ratio: 16 / 9;
		}
	}
`;

// Testimonials Styles
export const StyledTestimonials = styled.section`
	background: ${CSSVars.gray100};
	margin-left: -20px;
	margin-right: -20px;
	padding-left: 20px;
	padding-right: 20px;

	h2 {
		text-align: left;
		font-size: ${CSSVars.fontSize3XL};
		font-weight: 700;
		margin-bottom: 2rem;
		color: ${CSSVars.gray900};
	}

	.subtitle {
		text-align: left;
		font-size: ${CSSVars.fontSizeLG};
		color: ${CSSVars.gray500};
		margin-bottom: 3rem;
	}

	.testimonials-grid {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		gap: 30px;
	}

	.testimonial-card {
		background: ${CSSVars.white};
		padding: 30px;
		border-radius: 16px;
		box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
		text-align: left;

		.rating {
			display: flex;
			gap: 4px;
			margin-bottom: 2rem;

			.star {
				color: #ffc657;
				font-size: 1.25rem;
			}

			.star-empty {
				color: ${CSSVars.gray300};
			}
		}

		.testimonial-text {
			font-size: ${CSSVars.fontSizeSM};
			color: ${CSSVars.gray600};
			line-height: 1.7;
			margin-bottom: 1.5rem;
		}

		.author-info {
			display: flex;
			align-items: center;
			gap: 12px;

			.author-image {
				width: 48px;
				height: 48px;
				border-radius: 50%;
				object-fit: cover;
			}

			.author-details {
				.author-name {
					font-weight: 600;
					font-size: ${CSSVars.fontSizeSM};
					color: ${CSSVars.gray900};
				}

				.author-title {
					font-size: ${CSSVars.fontSizeXS};
					color: ${CSSVars.gray500};
				}
			}
		}
	}

	@media screen and (max-width: ${CSSVars.breakpointLG}) {
		.testimonials-grid {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	@media screen and (max-width: ${CSSVars.breakpointMD}) {
		.testimonials-grid {
			grid-template-columns: 1fr;
		}
	}
`;

// Content Section Styles
export const StyledContentSection = styled.section<{
	$alignment?: 'left' | 'center' | 'right';
	$position?: 'left' | 'right';
	$hasImage?: boolean;
}>`

	.content-wrapper {
		display: flex;
		align-items: center;
		gap: 60px;
		flex-direction: ${({ $position }) =>
			$position === 'right' ? 'row-reverse' : 'row'};
	}

	.text-content {
		flex: 1;
		text-align: left;

		.icon-wrapper {
			width: 50px;
			height: 50px;
			margin-bottom: 1.5rem;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			background: ${CSSVars.primary100};
			border-radius: 12px;

			img {
				width: 28px;
				height: 28px;
			}
		}

		h2 {
			font-size: ${CSSVars.fontSize3XL};
			font-weight: 700;
			margin-bottom: 2rem;
			color: ${CSSVars.gray900};
		}

		h3 {
			font-size: ${CSSVars.fontSizeXL};
			font-weight: 500;
			margin-bottom: 2rem;
			color: ${CSSVars.gray600};
		}

		.description {
			font-size: ${CSSVars.fontSizeMD};
			color: ${CSSVars.gray500};
			line-height: 1.7;
		}

		.buttons-wrapper {
			display: flex;
			gap: 1rem;
			margin-top: 2rem;
			justify-content: flex-start;
			flex-wrap: wrap;
		}
	}

	.image-wrapper {
		flex: 1;
		max-width: 550px;

		img {
			width: 100%;
			height: auto;
			border-radius: 12px;
			box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
		}

		figcaption {
			margin-top: 1rem;
			font-size: ${CSSVars.fontSizeSM};
			color: ${CSSVars.gray500};
			text-align: left;
		}
	}

	&.no-image {
		.text-content {
			max-width: 100%;
		}
	}

	@media screen and (max-width: ${CSSVars.breakpointLG}) {
		.content-wrapper {
			flex-direction: column;
		}

		.text-content {
			.buttons-wrapper {
				justify-content: flex-start;
			}
		}

		.image-wrapper {
			max-width: 100%;
		}
	}
`;

// Cards Section Styles (for content with multiple items)
export const StyledCardsSection = styled.section`
	h2 {
		text-align: left;
		font-size: ${CSSVars.fontSize3XL};
		font-weight: 700;
		margin-bottom: 2rem;
		color: ${CSSVars.gray900};
	}

	.subtitle {
		text-align: left;
		font-size: ${CSSVars.fontSizeLG};
		color: ${CSSVars.gray500};
		margin-bottom: 2rem;
	}

	.section-description {
		text-align: left;
		font-size: ${CSSVars.fontSizeMD};
		color: ${CSSVars.gray500};
		margin-bottom: 3rem;
		line-height: 1.7;
	}

	.cards-grid {
		display: grid;
		gap: 24px;

		&.cards-1 {
			grid-template-columns: 1fr;
			max-width: 400px;
		}

		&.cards-2 {
			grid-template-columns: repeat(2, 1fr);
		}

		&.cards-3 {
			grid-template-columns: repeat(3, 1fr);
		}
	}

	.card {
		background: ${CSSVars.white};
		border: 1px solid ${CSSVars.gray200};
		border-radius: 12px;
		overflow: hidden;
		box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
		transition: box-shadow 0.2s ease, transform 0.2s ease;
		display: flex;
		flex-direction: column;

		&:hover {
			box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
			transform: translateY(-2px);
		}

		.card-image {
			width: 100%;
			height: 200px;
			background: ${CSSVars.gray200};
			position: relative;
			flex-shrink: 0;

			img {
				width: 100%;
				height: 100%;
				object-fit: cover;
			}

			.card-image-placeholder {
				width: 100%;
				height: 100%;
				background: ${CSSVars.gray200};
			}
		}

		.card-content {
			padding: 20px;
			text-align: left;
			display: flex;
			flex-direction: column;
			flex: 1;

			h3 {
				font-size: ${CSSVars.fontSizeMD};
				font-weight: 600;
				margin-bottom: 0.5rem;
				color: ${CSSVars.gray900};
			}

			.card-subtitle {
				font-size: ${CSSVars.fontSizeSM};
				color: ${CSSVars.gray500};
				margin-bottom: 1rem;
			}

			.card-text {
				font-size: ${CSSVars.fontSizeSM};
				color: ${CSSVars.gray600};
				line-height: 1.6;
				margin-bottom: 1rem;
				flex: 1;
			}

			/* Style for CustomLink inside card */
			a {
				display: block;
				width: 100%;
				padding: 10px 16px;
				background: ${CSSVars.primary500};
				color: ${CSSVars.white};
				border-radius: 6px;
				font-weight: 500;
				font-size: ${CSSVars.fontSizeSM};
				text-align: center;
				transition: background 0.2s ease;
				margin-top: auto;

				&:hover {
					background: ${CSSVars.primary700};
				}
			}
		}
	}

	@media screen and (max-width: ${CSSVars.breakpointLG}) {
		.cards-grid.cards-3 {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	@media screen and (max-width: ${CSSVars.breakpointMD}) {
		.cards-grid.cards-2,
		.cards-grid.cards-3 {
			grid-template-columns: 1fr;
		}
	}
`;

// FAQ Section Styles
export const StyledFaqSection = styled.section`
	padding: 0;

	h2 {
		text-align: left;
		font-size: ${CSSVars.fontSize3XL};
		font-weight: 700;
		margin-bottom: 3rem;
		color: ${CSSVars.gray900};
	}

	.faq-section, .faq-section-content {
		margin: 0;
		padding: 0;
	}
`;

// Custom HTML Section Styles
export const StyledCustomHtmlSection = styled.section`

	h2 {
		text-align: left;
		font-size: ${CSSVars.fontSize3XL};
		font-weight: 700;
		margin-bottom: 2rem;
		color: ${CSSVars.gray900};
	}

	.custom-html-content {
		line-height: 1.7;
		color: ${CSSVars.gray600};

		/* Reset and style common HTML elements */
		p {
			margin-bottom: 1.5rem;
		}

		a {
			color: ${CSSVars.primary500};
			text-decoration: underline;

			&:hover {
				color: ${CSSVars.primary700};
			}
		}

		ul, ol {
			margin: 1.5rem 0;
			padding-left: 2rem;
		}

		ul {
			list-style: disc;
		}

		ol {
			list-style: decimal;
		}

		li {
			margin-bottom: 0.5rem;
		}

		img {
			max-width: 100%;
			height: auto;
			border-radius: 8px;
		}

		iframe {
			max-width: 100%;
			border-radius: 8px;
		}

		table {
			width: 100%;
			border-collapse: collapse;
			margin: 1.5rem 0;

			th, td {
				padding: 12px;
				border: 1px solid ${CSSVars.gray200};
				text-align: left;
			}

			th {
				background: ${CSSVars.gray100};
				font-weight: 600;
			}
		}

		blockquote {
			border-left: 4px solid ${CSSVars.primary500};
			padding-left: 1.5rem;
			margin: 1.5rem 0;
			font-style: italic;
			color: ${CSSVars.gray500};
		}

		h1, h2, h3, h4, h5, h6 {
			margin: 2rem 0 1rem;
			font-weight: 600;
			color: ${CSSVars.gray900};
		}

		h1 { font-size: ${CSSVars.fontSize4XL}; }
		h2 { font-size: ${CSSVars.fontSize3XL}; }
		h3 { font-size: ${CSSVars.fontSize2XL}; }
		h4 { font-size: ${CSSVars.fontSizeXL}; }
		h5 { font-size: ${CSSVars.fontSizeLG}; }
		h6 { font-size: ${CSSVars.fontSizeMD}; }
	}
`;
